/**
 * AiSettings — the entity's own AI assistant settings (singleton, edit-in-place).
 *
 * Mounted under EntitySettings (Organisation → Masters → Organisation → Settings).
 * Each organisation chooses its OWN provider (Anthropic, Amazon Bedrock, OpenAI), model and
 * credentials here. The server stores credentials encrypted and never sends them back — only whether
 * they are set and the last 4 characters.
 * API: GET/PUT /api/v1/ai/settings, DELETE /api/v1/ai/settings/key.
 *
 * Who: a hard-coded entitlement, NOT an RBAC grant — the entity's Entity Administrator or the Super Admin
 * (backend assistant/keys.py:require_ai_admin). Anyone else gets 403 and this block does not render.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Check } from 'lucide-react';
import Button from '../common/Button';
import FormControl from '../common/FormControl';
import Skeleton from '../common/Skeleton';
import Toast, { ToastMessage } from '../common/Toast';
import {
  AiProvider,
  AiSettings as AiSettingsType,
  AiSettingsUpdate,
  AiModelList,
  aiErrorText,
  deleteAiKey,
  getAiModels,
  getAiSettings,
  updateAiSettings,
} from '../../services/aiAssistantService';
import '../ai/AiAssistant.css';

const SECRET_LABEL: Record<string, string> = {
  api_key: 'API Key',
  aws_access_key_id: 'AWS Access Key ID',
  aws_secret_access_key: 'AWS Secret Access Key',
};

const AiSettings: React.FC = () => {
  const [forbidden, setForbidden] = useState(false);     // not the Entity Administrator / Super Admin
  const canModify = !forbidden;

  const [settings, setSettings] = useState<AiSettingsType | null>(null);
  const [provider, setProvider] = useState<AiProvider['value']>('anthropic');
  const [enabled, setEnabled] = useState(true);
  const [model, setModel] = useState('');
  const [region, setRegion] = useState('');
  const [secrets, setSecrets] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [models, setModels] = useState<AiModelList | null>(null);
  const [customModel, setCustomModel] = useState(false);
  const [toast, setToast] = useState<ToastMessage | null>(null);

  const load = (s: AiSettingsType) => {
    setSettings(s); setProvider(s.provider); setEnabled(s.is_enabled); setModel(s.model); setRegion(s.region || ''); setSecrets({});
  };

  const refresh = useCallback(async () => {
    try {
      load(await getAiSettings());
    } catch (err: any) {
      if (err?.response?.status === 403) { setForbidden(true); return; }
      setToast({ type: 'error', message: aiErrorText(err, 'Failed to load AI settings.') });
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const spec = useMemo(() => settings?.providers.find((p) => p.value === provider), [settings, provider]);

  // Every model the provider offers to this organisation's credentials (live); the one chosen is the default.
  useEffect(() => {
    if (!settings) return;
    let alive = true;
    setModels(null);
    getAiModels(provider).then((m) => { if (alive) setModels(m); }).catch(() => { if (alive) setModels(null); });
    return () => { alive = false; };
  }, [settings, provider]);
  const modelOptions = useMemo(() => {
    const list = models?.models ?? spec?.models ?? [];
    return model && !list.some((m) => m.value === model) ? [{ value: model, label: `${model} (current)` }, ...list] : list;
  }, [models, spec, model]);
  const switching = !!settings && provider !== settings.provider;
  const stored = !!settings?.has_key && !switching;

  const onProvider = (v: AiProvider['value']) => {
    setProvider(v);
    setSecrets({});
    const p = settings?.providers.find((x) => x.value === v);
    setModel(v === settings?.provider ? settings.model : (p?.models[0]?.value ?? ''));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canModify || !spec) return;
    setSaving(true);
    try {
      const data: AiSettingsUpdate = { provider, is_enabled: enabled, model: model.trim() };
      if (spec.needs_region) data.region = region.trim();
      for (const k of spec.secrets) {
        const v = (secrets[k] || '').trim();
        if (v) (data as any)[k] = v;
      }
      load(await updateAiSettings(data));
      setToast({ type: 'success', message: 'AI assistant settings updated.' });
    } catch (err) {
      setToast({ type: 'error', message: aiErrorText(err, 'Failed to save AI settings.') });
    } finally {
      setSaving(false);
    }
  };

  const removeKey = async () => {
    if (!canModify || !window.confirm('Remove the stored AI credentials? The assistant stops for everyone in this organisation until new ones are entered.')) return;
    try {
      load(await deleteAiKey());
      setToast({ type: 'delete', message: 'AI credentials removed.' });
    } catch (err) {
      setToast({ type: 'error', message: aiErrorText(err, 'Failed to remove the credentials.') });
    }
  };

  if (forbidden) return null;

  const live = !!settings?.has_key && settings.is_enabled && !!settings.providers.find((p) => p.value === settings.provider)?.available;
  return (
    <section className="ai-settings p-1" data-testid="SEC_org_settings_ai">
      <div className="ai-settings__head">
        <h3>AI Assistant</h3>
        {settings && (
          <span className={`ai-settings__state${live ? ' is-on' : ''}`}>
            {live ? 'On' : !settings.has_key ? 'Not set up' : !settings.is_enabled ? 'Switched off' : 'Provider not yet supported'}
          </span>
        )}
      </div>
      <p className="ai-settings__lead">
        The assistant on the purple strip at the bottom of every screen uses your organisation's own AI provider and credentials.
        It acts only with each person's own permissions, and every change it makes is confirmed by that person first.
        Only the Entity Administrator (or the Super Admin) can see and change these settings.
      </p>
      {!settings ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="flex flex-col gap-2"><Skeleton.Box width="40%" height={12} /><Skeleton.Box width="100%" height={36} radius={8} /></div>
          ))}
        </div>
      ) : (
        <form id="ai-settings-form" onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-4">
            <FormControl.SearchSelect
              label="Provider"
              value={provider}
              onChange={v => onProvider(String(v) as AiProvider['value'])}
              options={settings.providers.map(p => ({ value: p.value, label: p.label }))}
              disabled={!canModify}
              data-testid="SEL_org_settings_ai_provider"
            />
            <div className="flex flex-col gap-1">
              {!customModel ? (
                <FormControl.SearchSelect
                  label="Default Model"
                  value={model}
                  onChange={v => setModel(String(v))}
                  options={modelOptions}
                  disabled={!canModify}
                  helperText={models?.live ? `All ${models.models.length} models your key can use, from the provider.`
                    : (models?.note ?? spec?.model_hint ?? undefined)}
                  data-testid="SEL_org_settings_ai_model"
                />
              ) : (
                <FormControl.Input
                  label="Default Model"
                  type="text"
                  maxLength={160}
                  value={model}
                  onChange={e => setModel(e.target.value.trim())}
                  helperText="Any model id the provider accepts."
                  disabled={!canModify}
                  data-testid="INP_org_settings_ai_model"
                />
              )}
              {canModify && (
                <button type="button" className="ai-settings__link" onClick={() => setCustomModel(c => !c)} data-testid="BTN_org_settings_ai_model_mode">
                  {customModel ? 'Choose from the list' : 'Type another model id'}
                </button>
              )}
            </div>
            {spec?.needs_region && (
              <FormControl.Input
                label="AWS Region"
                type="text"
                maxLength={40}
                value={region}
                onChange={e => setRegion(e.target.value)}
                placeholder="ap-south-1"
                disabled={!canModify}
                data-testid="INP_org_settings_ai_region"
              />
            )}
            {spec?.secrets.map(k => (
              <FormControl.Input
                key={k}
                label={SECRET_LABEL[k] ?? k}
                type="password"
                autoComplete="off"
                maxLength={300}
                value={secrets[k] || ''}
                onChange={e => setSecrets(prev => ({ ...prev, [k]: e.target.value }))}
                placeholder={stored && settings.key_hint ? `${settings.key_hint} — type a new one to replace` : ''}
                helperText={stored && settings.key_hint ? `Stored: ${settings.key_hint} (first and last 4). Stored encrypted.`
                  : 'Stored encrypted; only its first and last 4 characters are shown after saving.'}
                disabled={!canModify}
                data-testid={`INP_org_settings_ai_${k}`}
              />
            ))}
            <div className="flex flex-col justify-center gap-2">
              <label className="ai-settings__row">
                <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)} disabled={!canModify}
                  data-testid="CHK_org_settings_ai_enabled" />
                Assistant switched on for this organisation
              </label>
              {stored && (
                <div className="ai-settings__row">
                  <span className="ai-settings__key-hint">Credentials {settings.key_hint}</span>
                  {canModify && <button type="button" className="ai-settings__remove" onClick={removeKey} data-testid="BTN_org_settings_ai_remove_key">Remove</button>}
                </div>
              )}
              {switching && settings.has_key && (
                <div className="ai-settings__row">Changing the provider replaces the stored credentials.</div>
              )}
              {spec && !spec.available && (
                <div className="ai-settings__row">This provider can be saved, but the assistant cannot use it yet.</div>
              )}
            </div>
          </div>
          {canModify && (
            <div className="flex justify-end mt-6">
              <Button type="submit" form="ai-settings-form" variant="update" icon={Check} disabled={saving} data-testid="BTN_org_settings_ai_submit">
                {saving ? 'Updating…' : 'Update'}
              </Button>
            </div>
          )}
        </form>
      )}
      <Toast toast={toast} onClose={() => setToast(null)} />
    </section>
  );
};

export default AiSettings;
